import { INPUT_EVENT_TYPE } from "~/utils/isManualInput";

const isDigit = (char: string): boolean => /\d/.test(char);

const countDigits = (value: string): number => value.replace(/\D/g, "").length;

/**
 * Formats a phone number to (###) ###-#### format.
 * Removes all non-digit characters and truncates to maximum 10 digits.
 * Returns partial formatting for incomplete numbers (useful for input fields).
 * 
 * @param phone - The phone number string to format
 * @returns Formatted phone string in (###) ###-#### format, or empty string if no digits found
 * 
 * @example
 * formatPhoneNumber("1234567890") // "(123) 456-7890"
 * formatPhoneNumber("123-456-7890") // "(123) 456-7890"
 * formatPhoneNumber("123") // "(123"
 * formatPhoneNumber("1234567") // "(123) 456-7"
 * formatPhoneNumber("12345678901234") // "(123) 456-7890" (truncates extra digits)
 * formatPhoneNumber("abc") // ""
 */
export const formatPhoneNumber = (phone: string): string => {
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, "");
    const truncated = cleaned.slice(0, 10);
    //Format to the "(###) ###-####"
    const match = truncated.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
        const part1 = match[1] ? `(${match[1]}` : "";
        const part2 = match[2] ? `) ${match[2]}` : "";
        const part3 = match[3] ? `-${match[3]}` : "";
        return `${part1}${part2}${part3}`.trim();
    }

    return "";
};

/**
 * Finds where the caret belongs in a reformatted phone number so editing in
 * the middle doesn't push it to the end. Keeps the caret after the same
 * number of digits it followed in the raw input.
 *
 * @param rawValue - The input value before formatting
 * @param rawCaret - The caret position in the raw value
 * @param formattedValue - The value after `formatPhoneNumber`
 *
 * @example
 * getFormattedPhoneCaret("(1293) 456", 3, "(129) 345-6") // 3
 * getFormattedPhoneCaret("(1234", 5, "(123) 4") // 7
 */
export const getFormattedPhoneCaret = (
    rawValue: string,
    rawCaret: number,
    formattedValue: string
): number => {
    const digitsBeforeCaret = countDigits(rawValue.slice(0, rawCaret));
    if (digitsBeforeCaret === 0) {
        // Past the "(" the caret stays there, so Backspace can reach the first digit.
        return rawCaret > 0 && formattedValue.length > 0 ? 1 : 0;
    }

    let digitsSeen = 0;
    for (let i = 0; i < formattedValue.length; i++) {
        if (isDigit(formattedValue.charAt(i))) digitsSeen++;
        if (digitsSeen === digitsBeforeCaret) return i + 1;
    }

    return formattedValue.length;
};

const findNeighbourDigit = (
    value: string,
    caret: number,
    forward: boolean
): number => {
    const step = forward ? 1 : -1;
    for (let i = forward ? caret : caret - 1; i >= 0 && i < value.length; i += step) {
        if (isDigit(value.charAt(i))) return i;
    }

    return -1;
};

/**
 * Formats a phone input edit and returns where the caret belongs afterwards.
 * Deleting a single separator ("(", ")", " " or "-") changes nothing once the
 * value is reformatted, which would leave the caret stuck in front of it, so
 * that keystroke deletes the neighbouring digit instead. A selection spanning
 * several characters is left as the user made it.
 *
 * @param rawValue - The input value after the edit, before formatting
 * @param rawCaret - The caret position in the raw value
 * @param previousValue - The value shown before the edit
 * @param inputType - The `inputType` of the native input event, if any
 *
 * @example
 * formatPhoneEdit("(123 456", 4, "(123) 456", "deleteContentForward") // { value: "(123) 56", caret: 4 }
 * formatPhoneEdit("(123)456", 5, "(123) 456", "deleteContentBackward") // { value: "(124) 56", caret: 3 }
 */
export const formatPhoneEdit = (
    rawValue: string,
    rawCaret: number,
    previousValue: string,
    inputType?: string
): { value: string; caret: number } => {
    let value = rawValue;
    let caret = rawCaret;

    const isSeparatorOnlyDelete =
        (inputType === INPUT_EVENT_TYPE.deleteContentBackward ||
            inputType === INPUT_EVENT_TYPE.deleteContentForward) &&
        previousValue.length - rawValue.length === 1 &&
        countDigits(rawValue) === countDigits(previousValue);

    if (isSeparatorOnlyDelete) {
        const digitIndex = findNeighbourDigit(
            rawValue,
            rawCaret,
            inputType === INPUT_EVENT_TYPE.deleteContentForward
        );

        if (digitIndex >= 0) {
            value = rawValue.slice(0, digitIndex) + rawValue.slice(digitIndex + 1);
            caret = Math.min(rawCaret, digitIndex);
        }
    }

    const formatted = formatPhoneNumber(value);

    return {
        value: formatted,
        caret: getFormattedPhoneCaret(value, caret, formatted),
    };
};

/**
 * Removes US country code (+1) from a phone number string.
 * Also trims leading and trailing whitespace.
 * 
 * @param phone - The phone number string that may contain +1 prefix
 * @returns Phone number without country code and trimmed
 * 
 * @example
 * removeCountryCode("+1234567890") // "234567890"
 * removeCountryCode("+1 (234) 567-8900") // "(234) 567-8900"
 * removeCountryCode("234567890") // "234567890"
 */
export const removeCountryCode = (phone: string): string => {
    return phone.replace(/\+1/g, "").trim();
};

/**
 * Formats a user's phone number by removing country code and applying standard formatting.
 * Combines removeCountryCode and formatPhoneNumber for complete processing.
 * Safe to use with null/undefined values.
 * 
 * @param phone - The phone number to format (accepts null/undefined)
 * @returns Formatted phone string in (###) ###-#### format, or empty string if input is null/undefined
 * 
 * @example
 * formatUserPhone("+1234567890") // "(234) 567-890"
 * formatUserPhone("1234567890") // "(123) 456-7890"
 * formatUserPhone("+1 234-567-8900") // "(234) 567-8900"
 * formatUserPhone(null) // ""
 * formatUserPhone(undefined) // ""
 */
export const formatUserPhone = (phone?: string | null): string => {
    if (!phone) return "";
    return formatPhoneNumber(removeCountryCode(phone));
};

/**
 * Converts a phone number to E.164 US format.
 * Removes all non-numeric characters and adds +1 if missing.
 *
 * @example
 * formatToE164PhoneNumber("(555) 123-4567") // "+15551234567"
 * formatToE164PhoneNumber("+1 (555) 123-4567") // "+15551234567"
 * formatToE164PhoneNumber("15551234567") // "+15551234567"
 */
export const formatToE164PhoneNumber = (phone: string | null): string => {
    if (!phone) return "";

    const digits = phone.replace(/\D/g, "");
    if (!digits) return "";

    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};